import cloneDeep from "lodash/cloneDeep";
import { isResponsiveValue } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { ResponsiveValue } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { BoxModelValues } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { ScreenSize } from "@circle-react-shared/ScreenSizeSelect";
import { areRawValuesEqual } from "./areRawValuesEqual";
import { isRecord, isStyleValue } from "./typePredicates";

const isStylePropertyValue = (
  value: unknown,
): value is string | number | BoxModelValues =>
  typeof value === "string" || typeof value === "number" || isRecord(value);

const updateResponsiveValue = (
  existingValue: ResponsiveValue,
  canvasSize: ScreenSize,
  newValue: unknown,
  cssVariables: Record<string, string>,
): ResponsiveValue => {
  const currentCanvasValue = existingValue[canvasSize];
  const areValuesEqual = areRawValuesEqual(
    currentCanvasValue,
    newValue,
    cssVariables,
  );

  if (areValuesEqual) {
    return existingValue;
  }

  return {
    ...existingValue,
    [canvasSize]: newValue,
  };
};

const createResponsiveValue = (
  existingValue: unknown,
  canvasSize: ScreenSize,
  newValue: unknown,
  cssVariables: Record<string, string>,
): ResponsiveValue => {
  const areValuesEqual = areRawValuesEqual(
    existingValue,
    newValue,
    cssVariables,
  );

  if (!isStylePropertyValue(existingValue)) {
    return {
      desktop: "",
      mobile: "",
      tablet: "",
    };
  }

  if (areValuesEqual) {
    return {
      desktop: existingValue,
      mobile: existingValue,
      tablet: existingValue,
    };
  }

  return {
    desktop: existingValue,
    mobile: existingValue,
    tablet: existingValue,
    [canvasSize]: newValue,
  };
};

export const mergeNewStylesWithCanvasSize = (
  styles: Record<string, unknown>,
  newStyles: unknown,
  cssVariables: Record<string, string>,
  canvasSize: ScreenSize,
): Record<string, unknown> => {
  if (!isRecord(newStyles)) return styles;

  const merged = cloneDeep(styles);

  for (const [key, value] of Object.entries(newStyles)) {
    const existingValue = styles[key];

    if (isStyleValue(existingValue)) {
      const existingValueObj = existingValue.value;
      const hasCanvasSizeStructure = isResponsiveValue(existingValueObj);

      if (hasCanvasSizeStructure) {
        const updatedValue = updateResponsiveValue(
          existingValueObj,
          canvasSize,
          value,
          cssVariables,
        );
        merged[key] = { ...existingValue, value: updatedValue };
      } else {
        const canvasSizeValue = createResponsiveValue(
          existingValueObj,
          canvasSize,
          value,
          cssVariables,
        );
        merged[key] = { ...existingValue, value: canvasSizeValue };
      }
    } else if (isRecord(value) && isRecord(existingValue)) {
      merged[key] = mergeNewStylesWithCanvasSize(
        existingValue,
        value,
        cssVariables,
        canvasSize,
      );
    } else {
      merged[key] = existingValue;
    }
  }

  return merged;
};
